import { createFileRoute, Link } from '@tanstack/react-router'
import { HelpLayout } from '@/components/help-center/help-layout'
import { HelpArticleContent } from '@/components/help-center/help-article'

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Import contacts from iPad',
  description: 'Use Safari on iPadOS to import contacts with the Contact Picker.',
  step: [
    { '@type': 'HowToStep', text: 'Enable the Contact Picker API flag in Safari settings.' },
    { '@type': 'HowToStep', text: 'Open MessageDrafts in Safari and choose Import from Phone.' },
    { '@type': 'HowToStep', text: 'Select contacts and confirm.' },
  ],
}

export const Route = createFileRoute('/_public/app/help/import-ipad')({
  component: ImportIpadPage,
  head: () => ({
    meta: [
      { title: 'Import Contacts from iPad | MessageDrafts Help' },
      {
        name: 'description',
        content:
          'Step-by-step instructions for importing contacts from iPad using Safari’s Contact Picker.',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(howToSchema),
      },
    ],
  }),
})

function ImportIpadPage() {
  return (
    <HelpLayout
      title="Import contacts from iPad"
      description="Use Safari on iPadOS to pull contacts directly into MessageDrafts."
    >
      <HelpArticleContent
        slug="import-ipad"
        title="Overview"
        description="This guide is for iPad users who want to import contacts from their device."
        tags={['ipad', 'import', 'contacts', 'safari']}
        devices={['ipad']}
      >
        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Prerequisites</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>iPad running iPadOS 17 or later.</li>
            <li>Safari browser with the Contact Picker API enabled.</li>
            <li>HTTPS connection to MessageDrafts.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Step-by-step</h3>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Open <strong>Settings → Safari → Advanced → Feature Flags</strong>.</li>
            <li>Enable <strong>Contact Picker API</strong>.</li>
            <li>Refresh MessageDrafts in Safari and go to the Import step.</li>
            <li>Tap <strong>Import from Phone</strong> and choose contacts.</li>
            <li>Confirm to continue.</li>
          </ol>
        </section>

        <figure className="space-y-2">
          <img
            src="/help/ipad-contact-picker.svg"
            alt="iPad Safari settings showing the Contact Picker API flag"
            className="w-full rounded-xl border border-slate-200"
          />
          <figcaption className="text-xs text-slate-500">
            Enable the Contact Picker API flag before importing.
          </figcaption>
        </figure>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Troubleshooting</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Import button missing:</strong> Confirm you are using Safari, not a
              desktop-mode browser.
            </li>
            <li>
              <strong>Contacts not showing:</strong> Check Safari contact permissions in
              Settings.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Alternative method</h3>
          <p>
            If the Contact Picker isn’t available, upload a CSV from your Contacts app. Review
            the <Link to="/app/help/import-android" className="underline">
              CSV formatting tips
            </Link>{' '}
            for the correct headers.
          </p>
        </section>
      </HelpArticleContent>
    </HelpLayout>
  )
}
